import { createContext, useState, useEffect, useCallback } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import axiosInstance from '@/utils/axios';

const initialState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
};

export const AuthContext = createContext({
  ...initialState,
  refreshUser: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

export function AuthProvider({ children }) {
  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();
  const { getToken } = useClerkAuth();
  const { signOut } = useClerk();

  const [state, setState] = useState(initialState);

  const fetchUserFromApi = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axiosInstance.get('/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setState({
        user: response.data,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      console.error('Failed to fetch user from /me:', error);
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: true,
      });
    }
  }, [getToken]);

  useEffect(() => {
    if (!clerkLoaded) {
      return;
    }

    if (isSignedIn && clerkUser) {
      fetchUserFromApi();
    } else {
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    }
  }, [clerkLoaded, isSignedIn, clerkUser, fetchUserFromApi]);

  const refreshUser = useCallback(async () => {
    if (isSignedIn) {
      setState(prev => ({ ...prev, isLoading: true }));
      await fetchUserFromApi();
    }
  }, [isSignedIn, fetchUserFromApi]);

  const logout = useCallback(async () => {
    await signOut();
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, [signOut]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
